import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useRef, useMemo } from 'react';
import { PropTypes as PropTypes$1 } from '@micromag/core';
import { getStyleFromColor, getOptimalImageUrl, getLayersFromBackground } from '@micromag/core/utils';
import { getSizeWithinBounds } from '@folklore/size';
import Video from '@micromag/element-video';

var styles = {"layers":"micromag-element-background-layers","layer":"micromag-element-background-layer","videoContainer":"micromag-element-background-videoContainer","video":"micromag-element-background-video","container":"micromag-element-background-container","right":"micromag-element-background-right","background":"micromag-element-background-background","bottom":"micromag-element-background-bottom"};

var propTypes$1 = {
  width: PropTypes.number,
  height: PropTypes.number,
  resolution: PropTypes.number,
  fit: PropTypes.oneOf(['contain', 'cover']),
  horizontalAlign: PropTypes.string,
  verticalAlign: PropTypes.string,
  repeat: PropTypes.bool,
  color: PropTypes$1.color,
  media: PropTypes.oneOfType([PropTypes$1.imageMedia, PropTypes$1.videoMedia]),
  mediaRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    current: PropTypes.any
  })]),
  className: PropTypes.string,
  playing: PropTypes.bool,
  muted: PropTypes.bool,
  children: PropTypes.node,
  loadingMode: PropTypes.string,
  shouldLoad: PropTypes.bool,
  withoutVideo: PropTypes.bool,
  forceMuted: PropTypes.bool
};
var defaultProps$1 = {
  width: null,
  height: null,
  resolution: 1,
  fit: null,
  horizontalAlign: 'center',
  verticalAlign: 'center',
  repeat: false,
  color: null,
  media: null,
  mediaRef: null,
  className: null,
  playing: false,
  muted: false,
  children: null,
  loadingMode: 'lazy',
  shouldLoad: true,
  withoutVideo: false,
  forceMuted: false
};
var Background = function Background(_ref) {
  var width = _ref.width,
    height = _ref.height,
    resolution = _ref.resolution,
    fit = _ref.fit,
    horizontalAlign = _ref.horizontalAlign,
    verticalAlign = _ref.verticalAlign,
    repeat = _ref.repeat,
    color = _ref.color,
    media = _ref.media,
    mediaRef = _ref.mediaRef,
    className = _ref.className,
    playing = _ref.playing,
    muted = _ref.muted,
    children = _ref.children,
    loadingMode = _ref.loadingMode,
    shouldLoad = _ref.shouldLoad,
    withoutVideo = _ref.withoutVideo,
    forceMuted = _ref.forceMuted;
  var _ref2 = media || {},
    _ref2$type = _ref2.type,
    mediaType = _ref2$type === void 0 ? null : _ref2$type,
    _ref2$metadata = _ref2.metadata,
    mediaMetadata = _ref2$metadata === void 0 ? null : _ref2$metadata,
    mediaThumbnailUrl = _ref2.thumbnail_url;
  var _ref3 = mediaMetadata || {},
    _ref3$width = _ref3.width,
    mediaWidth = _ref3$width === void 0 ? 0 : _ref3$width,
    _ref3$height = _ref3.height,
    mediaHeight = _ref3$height === void 0 ? 0 : _ref3$height;
  var isVideo = mediaType === 'video';
  var isImage = mediaType === 'image';

  // Lazy load
  var newShouldLoad = shouldLoad || loadingMode !== 'lazy';
  var wasLoadedRef = useRef(newShouldLoad);
  if (newShouldLoad && !wasLoadedRef.current) {
    wasLoadedRef.current = newShouldLoad;
  }
  var finalShouldLoad = wasLoadedRef.current;

  // color
  var containerStyle = _objectSpread({
    width: width,
    height: height
  }, getStyleFromColor(color));

  // image
  if (media !== null && (isImage || isVideo && (!shouldLoad || withoutVideo))) {
    var finalUrl = getOptimalImageUrl(isVideo ? {
      url: mediaThumbnailUrl
    } : media, width, height, {
      resolution: resolution
    });
    containerStyle.backgroundImage = finalUrl !== null && finalShouldLoad ? "url(\"".concat(finalUrl, "\")") : null;
    containerStyle.backgroundRepeat = repeat && !isVideo ? 'repeat' : 'no-repeat';
    containerStyle.backgroundPosition = [horizontalAlign, verticalAlign].join(' ');
    if (fit !== null) {
      containerStyle.backgroundSize = fit;
    } else if (!repeat || isVideo) {
      containerStyle.backgroundSize = 'cover';
    }
  }

  // video
  var videoContainerStyle = {};
  if (isVideo && shouldLoad) {
    if (width > 0 && height > 0) {
      var _getSizeWithinBounds = getSizeWithinBounds(mediaWidth, mediaHeight, width, height, {
          cover: fit === 'cover' || fit === null
        }),
        _getSizeWithinBounds$ = _getSizeWithinBounds.width,
        videoWidth = _getSizeWithinBounds$ === void 0 ? 0 : _getSizeWithinBounds$,
        _getSizeWithinBounds$2 = _getSizeWithinBounds.height,
        videoHeight = _getSizeWithinBounds$2 === void 0 ? 0 : _getSizeWithinBounds$2;
      videoContainerStyle.width = videoWidth;
      videoContainerStyle.height = videoHeight;
      videoContainerStyle.left = -(videoWidth - width) / 2;
      videoContainerStyle.top = -(videoHeight - height) / 2;
    } else {
      videoContainerStyle.objectFit = 'cover';
    }
  }
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty({}, className, className !== null)]),
    style: containerStyle
  }, isVideo && shouldLoad && !withoutVideo ? /*#__PURE__*/React.createElement("div", {
    className: styles.videoContainer,
    style: videoContainerStyle
  }, /*#__PURE__*/React.createElement(Video, {
    className: styles.video,
    media: media,
    mediaRef: !forceMuted ? mediaRef : null,
    paused: !playing,
    muted: muted || forceMuted,
    shouldLoad: shouldLoad,
    disablePictureInPicture: true,
    autoPlay: true,
    loop: true,
    withPoster: true
  })) : null, /*#__PURE__*/React.createElement("div", {
    className: styles.content
  }, children));
};
Background.propTypes = propTypes$1;
Background.defaultProps = defaultProps$1;

var _excluded = ["horizontalAlign", "verticalAlign"];
var propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  resolution: PropTypes.number,
  background: PropTypes.oneOfType([PropTypes$1.backgroundElement, PropTypes.arrayOf(PropTypes$1.backgroundElement)]),
  playing: PropTypes.bool,
  muted: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  layerClassName: PropTypes.string,
  backgroundClassName: PropTypes.string,
  loadingMode: PropTypes.string,
  mediaRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    current: PropTypes.any
  })]),
  shouldLoad: PropTypes.bool,
  withoutVideo: PropTypes.bool
};
var defaultProps = {
  width: null,
  height: null,
  resolution: 1,
  background: [],
  playing: false,
  muted: false,
  children: null,
  className: null,
  layerClassName: null,
  backgroundClassName: null,
  loadingMode: 'lazy',
  mediaRef: null,
  shouldLoad: true,
  withoutVideo: false
};
var BackgroundLayers = function BackgroundLayers(_ref) {
  var width = _ref.width,
    height = _ref.height,
    resolution = _ref.resolution,
    background = _ref.background,
    playing = _ref.playing,
    muted = _ref.muted,
    children = _ref.children,
    className = _ref.className,
    layerClassName = _ref.layerClassName,
    backgroundClassName = _ref.backgroundClassName,
    loadingMode = _ref.loadingMode,
    mediaRef = _ref.mediaRef,
    shouldLoad = _ref.shouldLoad,
    withoutVideo = _ref.withoutVideo;
  var hasSize = width > 0 && height > 0;
  var layers = useMemo(function () {
    return getLayersFromBackground(background);
  }, [background]);
  var maxZIndex = layers.length;
  if (layers.length === 0) {
    return null;
  }

  // color
  var containerStyle = _objectSpread({}, hasSize ? {
    width: width,
    height: height
  } : null);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty({}, className, className !== null)]),
    style: containerStyle
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.layers
  }, layers.map(function (_ref3, index) {
    var _ref3$horizontalAlign = _ref3.horizontalAlign,
      horizontalAlign = _ref3$horizontalAlign === void 0 ? undefined : _ref3$horizontalAlign,
      _ref3$verticalAlign = _ref3.verticalAlign,
      verticalAlign = _ref3$verticalAlign === void 0 ? undefined : _ref3$verticalAlign,
      layer = _objectWithoutProperties(_ref3, _excluded);
    return /*#__PURE__*/React.createElement("div", {
      key: "background-".concat(index),
      className: classNames([styles.layer, _defineProperty(_defineProperty(_defineProperty({}, styles.bottom, verticalAlign === 'bottom'), styles.right, horizontalAlign === 'right'), layerClassName, layerClassName !== null)]),
      style: {
        zIndex: maxZIndex - index
      }
    }, /*#__PURE__*/React.createElement(Background, Object.assign({
      width: width,
      height: height,
      resolution: resolution,
      className: classNames([styles.background, _defineProperty({}, backgroundClassName, backgroundClassName !== null)]),
      playing: playing,
      muted: muted,
      horizontalAlign: horizontalAlign,
      verticalAlign: verticalAlign,
      loadingMode: loadingMode,
      shouldLoad: shouldLoad,
      mediaRef: mediaRef,
      withoutVideo: withoutVideo
    }, layer)));
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.content
  }, children));
};
BackgroundLayers.propTypes = propTypes;
BackgroundLayers.defaultProps = defaultProps;

export { Background, BackgroundLayers, BackgroundLayers as default };
